"use client";

import clsx from "clsx";
import React, { HTMLInputTypeAttribute, useMemo, useRef } from "react";
import {
  FieldValues,
  Path,
  PathValue,
  RegisterOptions,
  UseFormRegister,
  Validate,
  ValidationRule,
} from "react-hook-form";

import { VALIDATION_MESSAGE } from "constants/validation";

const Input = <T extends FieldValues>({
  id,
  name,
  register,
  required = false,
  errorMessage = VALIDATION_MESSAGE.REQUIRED,
  placeholder,
  type = "text",
  inputClassName = "",
  validationPattern,
  validation,
  isDisabled = false,
  isError = false,
  innerPadding = "pl-4",
  maxLength,
  maxNumber,
  minNumber = 0,
  noBorder = false,
  height = "h-12",
  fontSize,
  hardStrictMaxLength,
  handleBlur,
  exceptPattern,
  onChange,
  dataTestId,
}: {
  id?: string;
  name: Path<T>;
  dataTestId?: string;
  register: UseFormRegister<T>;
  required?: boolean;
  errorMessage?: string;
  inputClassName?: string;
  placeholder?: string;
  type?: HTMLInputTypeAttribute;
  validationPattern?: ValidationRule<RegExp>;
  validation?: Validate<PathValue<T, Path<T>>, T> | Record<string, Validate<PathValue<T, Path<T>>, T>> | undefined;
  isDisabled?: boolean;
  isError?: boolean;
  innerPadding?: string;
  maxLength?: number;
  maxNumber?: number;
  minNumber?: number;
  noBorder?: boolean;
  height?: string;
  fontSize?: string;
  hardStrictMaxLength?: number;
  handleBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  exceptPattern?: RegExp;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const requiredOptions = useMemo(() => {
    const baseOptions = {
      required: { value: false, message: errorMessage },
    };

    return required
      ? maxLength
        ? {
            ...baseOptions,
            required: { value: true, message: errorMessage },
            maxLength: { value: maxLength, message: VALIDATION_MESSAGE.TOO_LARGE_STRING(maxLength) },
          }
        : maxNumber
        ? {
            ...baseOptions,
            required: { value: true, message: errorMessage },
            max: { value: maxNumber, message: VALIDATION_MESSAGE.TOO_LARGE_NUMBER(maxNumber) },
          }
        : { ...baseOptions, required: { value: true, message: errorMessage } }
      : maxLength
      ? { ...baseOptions, maxLength: { value: maxLength, message: VALIDATION_MESSAGE.TOO_LARGE_STRING(maxLength) } }
      : maxNumber
      ? { ...baseOptions, max: { value: maxNumber, message: VALIDATION_MESSAGE.TOO_LARGE_NUMBER(maxNumber) } }
      : baseOptions;
  }, [required, errorMessage, maxLength, maxNumber]);

  const colorClasses = isDisabled
    ? "bg-form-disabledBackground text-gray border-gray cursor-not-allowed"
    : isError
    ? "bg-form-isErrorBackGround border-attention"
    : "bg-input-normal border-input-normal";

  const switchConditionByInputType: RegisterOptions<T, Path<T>> = type === "number"
    ? // DOCS number型の場合にpatternが不要
      {
        ...requiredOptions,
        validate: validation,
        valueAsNumber: true, //DOCS hook-formのregisterにnumber型を指定しても、inputのvalueがstringになるので、numberに変換する
      }
    : {
        ...requiredOptions,
        pattern: validationPattern,
        validate: validation,
      };

  // 数字の場合は、スクロールしたら、数字が増減する現象を防ぐ。
  const onWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    if (event.target instanceof HTMLInputElement) {
      const inputElement = event.target;
      if (inputElement.type === "number") {
        inputElement.blur();
      }
    }
  };

  //キーボードバリデーション
  const exceptThisSymbols = ["e", "E", "+", "-", "."];

  // ✅ Allow control keys
  const controlKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "Escape",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
    "PageUp",
    "PageDown",
  ];

  const keyBoardValidation = (e: React.KeyboardEvent) => {
    // ✅ 1. Allow control keys FIRST (highest priority)
    if (controlKeys.includes(e.key)) {
      return;
    }

    // ✅ 2. Allow keyboard shortcuts (Ctrl/Cmd + key)
    // This includes Ctrl+V, Ctrl+C, Ctrl+X, Ctrl+A, etc.
    if (e.ctrlKey || e.metaKey) {
      return;
    }

    // ✅ 3. Check exceptPattern only for printable single characters
    // Skip during IME composition — handled by onCompositionEnd instead
    if ((e.nativeEvent as KeyboardEvent).isComposing) return;

    if (exceptPattern && e.key.length === 1 && !exceptPattern.test(e.key)) {
      e.preventDefault();
      return;
    }

    // ✅ 4. Block special symbols for number type
    if (type === "number" && exceptThisSymbols.includes(e.key)) {
      e.preventDefault();
    }
  };

  //Prevent paste of invalid characters
  const reg = /[eE+\-.]/;

  // ✅ Strip IME-composed characters that don't match exceptPattern
  const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
    if (!exceptPattern) return;
    const input = e.target as HTMLInputElement;
    const filtered = input.value
      .split("")
      .filter((char) => exceptPattern.test(char))
      .join("");
    if (filtered !== input.value) {
      // Update the DOM value and trigger a native input event so react-hook-form picks up the change
      const nativeValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
      nativeValueSetter?.call(input, filtered);
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const clipboardData = e.clipboardData.getData("Text");

    // ✅ Check exceptPattern first
    if (exceptPattern) {
      // Check if ALL characters match the pattern
      const isValid = clipboardData.split("").every((char) => exceptPattern.test(char));

      if (!isValid) {
        e.preventDefault();
        return;
      }
    }

    // ✅ Check number type restrictions
    if (type === "number") {
      if (reg.test(clipboardData)) {
        e.preventDefault();
      }
    }
  };

  // Track IME composition to prevent duplicate characters on Windows
  const isComposingRef = useRef(false);

  // register the field once and combine handlers when rendering
  const registered = register(name, switchConditionByInputType) as ReturnType<UseFormRegister<T>>;

  return (
    <>
      <input
        {...registered}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          // call react-hook-form's onChange first
          try {
            registered?.onChange?.(e);
          } catch (err) {
            // noop
          }
          // Skip external onChange during IME composition to prevent
          // duplicate characters on Windows (Japanese Microsoft IME, etc.)
          if (!isComposingRef.current) {
            onChange?.(e);
          }
        }}
        className={clsx(
          "w-full rounded border placeholder:text-input-placeholder hover:border-black focus:border-link focus:bg-link-light focus:shadow-input focus:outline-none",
          noBorder ? "border-none" : "",
          innerPadding,
          colorClasses,
          height,
          fontSize,
          inputClassName
        )}
        defaultValue={undefined}
        placeholder={placeholder}
        disabled={isDisabled}
        type={type}
        id={id}
        // Enable if you want to use the input maxLength
        // type={"input"}
        onWheel={onWheel}
        onKeyDown={keyBoardValidation}
        onPaste={handlePaste}
        onCompositionStart={() => {
          isComposingRef.current = true;
        }}
        onCompositionEnd={(e) => {
          isComposingRef.current = false;
          handleCompositionEnd(e);
        }}
        maxLength={hardStrictMaxLength}
        data-testid={dataTestId}
        {...(handleBlur && { onBlur: handleBlur })}
        {...(type === "number" && { min: minNumber })}
      />
    </>
  );
};

export default Input;
